import React, { useCallback } from 'react'
import cx from 'classnames'
import { useDevice } from 'device'
import { openModal } from 'modals'

import { Text } from 'components/dataDisplay'
import { Bone, ScrollableContainer, WidthContainer } from 'components/layout'

import PlanCard from './components/PlanCard/PlanCard'

import messages from './messages'
import s from './UpgradeLargeBlock.scss'


const orderClassNameByProductCount = {
  mobile: {
    1: 'order-3',
    2: 'order-1',
    3: 'order-2',
  },
  desktop: {
    1: 'order-3',
    2: 'order-2',
    3: 'order-1',
  },
}

type UpgradeLargeBlockProps = {
  className?: string
  title?: Intl.Message
  text?: Intl.Message
  plans: SubscriptionModule.UpgradePlan[]
  isFetching?: boolean
  fullWidth?: boolean
  onPlanSelect: (plan: SubscriptionModule.UpgradePlan) => void
} & AtLeastOne<{
  isSubscriptionFlow: boolean
  onConfirm: (plan: SubscriptionModule.UpgradePlan) => void
}>

const UpgradeLargeBlock: React.FC<UpgradeLargeBlockProps> = (props) => {
  const { className, title, text, plans, isFetching, isSubscriptionFlow, fullWidth = false, onPlanSelect, onConfirm } = props

  const { isMobile } = useDevice()

  const handlePlanSelect = useCallback((plan: SubscriptionModule.UpgradePlan) => {
    if (typeof onPlanSelect === 'function') {
      onPlanSelect(plan)
    }

    if (!isSubscriptionFlow) {
      openModal('upgradeSubscriptionSummaryModal', {
        planName: plan.name,
        onConfirm: () => onConfirm(plan),
      })
    }
  }, [ onPlanSelect, onConfirm, isSubscriptionFlow ])


  if (isFetching) {
    return (
      <Bone h={400} bgColor="light-beige" />
    )
  }

  // should be at least 2 - current and with higher value
  if (!plans || plans.length < 2) {
    return null
  }

  const itemOrderClassNameMap = isMobile
    ? orderClassNameByProductCount.mobile
    : isSubscriptionFlow ? null : orderClassNameByProductCount.desktop

  return (
    <div className={cx(className, 'bg-light-beige', isMobile ? 'py-64' : `pt-72 pb-96 ${fullWidth ? '' : 'max-w-1440 mx-auto'} ${s.bg}`)}>
      <WidthContainer className="text-center">
        <Text message={title || messages.title} style="h4" />
        <Text className="mt-16 max-w-560 mx-auto" message={text || messages.text} style="p2" />
        <ScrollableContainer className={cx('mt-40', isMobile ? s.container : 'pb-80')}>
          <div className={isMobile ? `inline-grid grid-flow-col ${s.grid} gap-16` : 'grid grid-col-3 gap-16'}>
            {
              plans.map((data) => (
                <PlanCard
                  key={data.id}
                  className={cx(isMobile && s.item, itemOrderClassNameMap?.[data.productsPerPeriod])}
                  data={data}
                  isSubscriptionFlow={isSubscriptionFlow}
                  onClick={handlePlanSelect}
                />
              ))
            }
          </div>
        </ScrollableContainer>
      </WidthContainer>
    </div>
  )
}


export default UpgradeLargeBlock
